import { useIntl } from 'react-intl';
import { Form, Row, Col, FormInstance, Button, Divider } from 'antd';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import ItemsStockModal from '@/components/_common/ItemsStockModal';
import {
  StockItem,
  StockItemType,
} from '@/core/domain/stock/StockItems/StockItems.types';
import PurchaseItemsPurchaseList from './PurchaseItemsPurchaseList';
import SituationFormField from '@/components/Movements/DirectSale/SituationFormField';
import {
  Purchase,
  PurchaseItemType,
} from '@/core/domain/purchase/Purchase.types';
import { usePurchaseListItemCreate } from '@/core/domain/purchase/purchaseItem/PurchaseItem.hooks';
import { useDisclosure } from '@/hooks';
import {
  RequestPurchaseItemType,
  RequestPurchases,
} from '@/core/domain/workorder/RequestPurchase/RequestPurchase.types';
import ItemsRequestPurchaseModal from '@/components/_common/ItemsRequestPurchase';
import { CreatePurchaseItemRequest } from '@/core/domain/purchase/purchaseItem/PurchaseItem.types';
import { useReleasePurchaseFormContext } from '@/pages/MovementsPage/PurchasesPage/ReleasesPage/ReleasesPurchaseProvider';
import { FormPurchaseItemSituation } from '@/pages/MovementsPage/PurchasesPage/ReleasesPage/PurchaseDetailsPage/PurchaseTaxesPage';
import Authorities from '@/types/Authority';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import PurcheseAlertBoshMigration from '../../PurchaseAlertBoshMigration';
import { Status } from '@/utils/enum/statusEnum';

interface Props {
  form: FormInstance<FormPurchaseItemSituation>;
  purchaseId: number;
  purchase?: Purchase;
}

function PurchaseItemsPurchase({ form, purchaseId, purchase }: Props) {
  const intl = useIntl();
  const history = useHistory();
  const [stockItemModalOpened, stockItemModalAction] = useDisclosure();
  const [requestPurchaseModalOpened, requestPurchaseModalVisibleAction] =
    useDisclosure();
  const [itemType, setItemType] = useState<StockItemType>();

  const { situation, itemsPurchase } = useReleasePurchaseFormContext();

  const { isLoading, mutateAsync: createItemListMutate } =
    usePurchaseListItemCreate();

  const isCanceled = purchase?.situation === Status.canceled;

  const handleSelectItemModal = (itemType: StockItemType) => {
    setItemType(itemType);

    stockItemModalAction.open();
  };

  const handleSelectItem = (item?: StockItem) => {
    if (item?.partId) {
      history.push(`itemsPurchase/createItemPurchase/part/${item.partId}`);
    } else if (item?.consumableId) {
      history.push(
        `itemsPurchase/createItemPurchase/consumable/${item.consumableId}`,
      );
    } else {
      history.push(`itemsPurchase/createItemPurchase/task`);
    }

    return item;
  };

  const handleSelectRequestPurchases = async (
    requestPurchase?: RequestPurchases[],
  ) => {
    if (requestPurchase) {
      const obj: CreatePurchaseItemRequest[] = requestPurchase.map((item) => ({
        purchase: purchaseId,
        partId:
          item.type === RequestPurchaseItemType.part ? item.item.id : undefined,
        type:
          item.type === RequestPurchaseItemType.part
            ? PurchaseItemType.part
            : PurchaseItemType.task,
        workOrderItemId: item.workItemId,
        workOrderNumber: item.workOrderNumber,
        complementId: item.complementId,
        manufacturerId: item.item?.manufacturerId,
        quantity: item.total,
        totalAmount: 0,
        unitaryValue: item.workOrderUnitCost,
        unitMeasurement: RequestPurchaseItemType.task
          ? item.item?.unit || 'hr'
          : item.item.unit,
        name: item.description,
        workOrderPurchaseQuotations: {
          id: item.id,
          quantitypurchased: item.quantityPurchased,
          quantitySentPurchase: item.quantitySentPurchased,
          requestedQuantity: item.requestedQuantity,
        },
        ncmId: item.item?.ncmId,
        cfopId: item.item?.cfopId,
      }));

      await createItemListMutate(obj);

      requestPurchaseModalVisibleAction.close();
    }

    return requestPurchase;
  };

  return (
    <>
      <Form layout="vertical" form={form}>
        <Row gutter={[16, 9]}>
          <Col span={24}>
            <SituationFormField initialValue={situation} />
          </Col>

          {purchase?.boschMigration && (
            <Col span={24}>
              <PurcheseAlertBoshMigration />
            </Col>
          )}

          <Col span={24} style={{ textAlign: 'right' }}>
            <SpaceForButton>
              <Button
                block
                size="middle"
                onClick={() => handleSelectItemModal(StockItemType.Part)}
                disabled={
                  isCanceled ||
                  purchase?.boschMigration ||
                  !hasPermissions([
                    Authorities.REGISTER_PART_VIEW,
                    Authorities.MOVIMENTATION_PURCHASE_ITEM_ADD,
                  ])
                }
              >
                {intl.formatMessage({
                  id: 'purchases.details.itemsPurchase.selectPart',
                })}
              </Button>

              <Button
                block
                size="middle"
                onClick={() => handleSelectItemModal(StockItemType.Consumable)}
                disabled={
                  isCanceled ||
                  purchase?.boschMigration ||
                  !hasPermissions([
                    Authorities.REGISTER_CONSUMABLE_VIEW,
                    Authorities.MOVIMENTATION_PURCHASE_ITEM_ADD,
                  ])
                }
              >
                {intl.formatMessage({
                  id: 'purchases.details.itemsPurchase.selectConsumable',
                })}
              </Button>

              <Button
                block
                size="middle"
                onClick={() => handleSelectItem(undefined)}
                disabled={
                  isCanceled ||
                  purchase?.boschMigration ||
                  !hasPermissions([
                    Authorities.REGISTER_TASK_VIEW,
                    Authorities.MOVIMENTATION_PURCHASE_ITEM_ADD,
                  ])
                }
              >
                {intl.formatMessage({
                  id: 'purchases.details.itemsPurchase.selectService',
                })}
              </Button>

              <Button
                block
                size="middle"
                onClick={requestPurchaseModalVisibleAction.open}
                disabled={
                  isCanceled ||
                  purchase?.boschMigration ||
                  !hasPermissions([Authorities.MOVIMENTATION_PURCHASE_ITEM_ADD])
                }
              >
                {intl.formatMessage({
                  id: 'purchases.details.itemsPurchase.selectItemsOS',
                })}
              </Button>
            </SpaceForButton>
          </Col>
          <Divider />

          {hasPermissions([Authorities.MOVIMENTATION_PURCHASE_ITEM_READ]) && (
            <>
              <Col span={24}>
                <PurchaseItemsPurchaseList
                  isCanceled={isCanceled}
                  purchaseId={purchaseId}
                  purchase={purchase}
                />
              </Col>
            </>
          )}
        </Row>
      </Form>

      <ItemsStockModal
        itemsSelected={itemsPurchase?.map((items) => items.consumableId)}
        handleVisible={stockItemModalAction.close}
        onSelectItem={handleSelectItem}
        visible={stockItemModalOpened}
        itemType={itemType}
        isPurchase
      />

      {!!purchase?.companyId && (
        <ItemsRequestPurchaseModal
          handleVisible={requestPurchaseModalVisibleAction.toggle}
          finishCallback={handleSelectRequestPurchases}
          visible={requestPurchaseModalOpened}
          loading={isLoading}
          companyId={purchase.companyId}
          purchaseItemsRequestPurchaseIdsSelected={itemsPurchase?.map(
            (item) => item.workOrderItemId,
          )}
        />
      )}
    </>
  );
}

export default PurchaseItemsPurchase;
